import React, { useEffect } from "react";
import { usePageHeaderTrail } from "./PageHeaderTrailContext";

/**
 * Shared page header for every module in the dashboard.
 *
 * Style language: matches DashboardEcommerce/Section.js (gradient banner)
 * but with a flatter, more form-oriented variant. Stays inside the
 * existing Velzon/Skote theme — uses theme typography utilities
 * (fs-22, ff-secondary, text-muted) and Remix icons (ri-*).
 *
 * Originally built for the MAFIA Aux + Digital Inspection visual upgrade
 * pass and exported there as `MafiaPageHeader`. Renamed and moved to
 * `src/Components/Common/DashboardKit/` so other modules (EPO, DMS, HR,
 * Contract Review, Attendance, etc.) can reuse it without an awkward
 * `import from "../Mafia/_shared"` path.
 *
 * Props
 *  - icon:        ri-* icon class (defaults to ri-stack-line)
 *  - eyebrow:     small uppercase line above the title
 *  - title:       page title (required)
 *  - subtitle:    smaller helper line under title
 *  - breadcrumb:  array of { label, to?, onClick? } — last item rendered as
 *                 active text. Round 17: a "Workflows" root crumb linking
 *                 /workflows is auto-prepended to every trail (owner ask:
 *                 a way back to the launcher from every page), deduped when
 *                 the caller already starts with Workflows. onClick crumbs
 *                 support in-component navigation (MenuHub drill-down).
 *  - actions:     React node — buttons rendered on the right
 *  - tone:        "default" (indigo) | "blue" | "violet" | "teal" | "amber" | "rose"
 *  - compact:     bool — uses tighter vertical padding (for sub-pages)
 */
const TONES = {
  default: {
    bg: "linear-gradient(135deg, #4051b5 0%, #6d5fda 100%)",
    accent: "#a5b1ff",
  },
  blue: {
    bg: "linear-gradient(135deg, #0ab39c 0%, #299cdb 100%)",
    accent: "#9bd9ff",
  },
  violet: {
    bg: "linear-gradient(135deg, #6f42c1 0%, #9c5bff 100%)",
    accent: "#dac1ff",
  },
  teal: {
    bg: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)",
    accent: "#a8efe6",
  },
  amber: {
    bg: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)",
    accent: "#fde4a3",
  },
  rose: {
    bg: "linear-gradient(135deg, #be123c 0%, #f43f5e 100%)",
    accent: "#fdc4cf",
  },
};

const DashboardPageHeader = ({
  icon = "ri-stack-line",
  eyebrow,
  title,
  subtitle,
  breadcrumb,
  actions,
  tone = "default",
  compact = false,
}) => {
  const t = TONES[tone] || TONES.default;
  const padY = compact ? "16px 24px" : "22px 28px";

  // Round 22 — the trail no longer renders inside the page (it scrolled
  // away with the banner). It is published to the fixed topbar instead,
  // so the page name + clickable trail stay visible while scrolling.
  // Canonical order (Round 18) and the universal fallback (Round 19)
  // are computed here exactly as before.
  const { setTrail } = usePageHeaderTrail();
  const HOME_LABELS = ["dashboard", "dashboards", "applications", "home"];
  const trailSrc = (breadcrumb && breadcrumb.length > 0) ? breadcrumb : [{ label: title }];
  const provided = trailSrc.filter(
    (c, i) => !(i === 0 && c && HOME_LABELS.includes(String(c.label).toLowerCase()))
  );
  const roots = [{ label: "Dashboard", to: "/dashboard" }];
  if (!(provided[0] && provided[0].label === "Workflows")) {
    roots.push({ label: "Workflows", to: "/workflows" });
  }
  const crumbs = [...roots, ...provided];
  const trailSig = crumbs.map((c) => `${c.label}|${c.to || (c.onClick ? "fn" : "")}`).join(">");
  useEffect(() => {
    setTrail({ title, crumbs });
    return () => setTrail(null);
    // crumbs identity changes every render; trailSig captures real changes.
  }, [title, trailSig]);

  return (
    <div className="mafia-page-header mb-3">
      <div
        className="mafia-page-header__banner"
        style={{
          background: t.bg,
          borderRadius: "16px",
          padding: padY,
          color: "#fff",
          boxShadow: "0 6px 18px rgba(33, 37, 41, 0.08)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* decorative shape — same pattern as DashboardJob widgets */}
        <svg
          viewBox="0 0 200 200"
          width="220"
          height="220"
          style={{
            position: "absolute",
            right: -40,
            top: -40,
            opacity: 0.12,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        >
          <circle cx="100" cy="100" r="90" fill="#fff" />
        </svg>
        <svg
          viewBox="0 0 200 200"
          width="160"
          height="160"
          style={{
            position: "absolute",
            right: 80,
            bottom: -60,
            opacity: 0.08,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        >
          <circle cx="100" cy="100" r="90" fill="#fff" />
        </svg>

        <div
          className="d-flex flex-wrap align-items-center justify-content-between gap-3"
          style={{ position: "relative", zIndex: 1 }}
        >
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            <div
              className="d-inline-flex align-items-center justify-content-center"
              style={{
                width: compact ? 44 : 52,
                height: compact ? 44 : 52,
                borderRadius: 12,
                background: "rgba(255,255,255,0.18)",
                flexShrink: 0,
              }}
            >
              <i
                className={icon}
                style={{ fontSize: compact ? 22 : 26, color: "#fff" }}
              />
            </div>

            <div className="flex-grow-1" style={{ minWidth: 0 }}>
              {eyebrow ? (
                <div
                  className="text-uppercase fw-medium"
                  style={{
                    fontSize: 11,
                    letterSpacing: "0.08em",
                    color: t.accent,
                    marginBottom: 2,
                  }}
                >
                  {eyebrow}
                </div>
              ) : null}

              <h4
                className="ff-secondary fw-semibold mb-0"
                style={{
                  color: "#fff",
                  fontSize: compact ? 20 : 22,
                  letterSpacing: "-0.01em",
                }}
              >
                {title}
              </h4>

              {subtitle ? (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 13,
                    color: "rgba(255,255,255,0.85)",
                  }}
                >
                  {subtitle}
                </div>
              ) : null}
            </div>
          </div>

          {actions ? (
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {actions}
            </div>
          ) : null}
        </div>
      </div>


    </div>
  );
};

export default DashboardPageHeader;
